<script setup>
import { computed, onMounted, onBeforeUnmount, reactive, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Button, EmptyState, FormField, Input, Modal, PageHeader, Pagination, Section, Select, Spinner, Table, Toolbar } from '@drama/ui'
import { getConversionFunnelTrafficVideos, getTrafficVideoHistory } from '../api/analytics.js'
import { calendar_report_range } from '../lib/local-date-range.js'
import DashboardTrend from '../components/DashboardTrend.vue'

const { t, locale } = useI18n()
const route = useRoute()
const text = (key, params = {}) => t(`visualGameAdmin.socialVideos.${key}`, params)
const platforms = ['tiktok', 'youtube', 'instagram', 'facebook', 'x']
const names = { tiktok: 'TikTok', youtube: 'YouTube', instagram: 'Instagram', facebook: 'Facebook', x: 'X' }
function day(offset) {
  const date = new Date()
  date.setDate(date.getDate() + offset)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}
function initialDate(value, fallback) { return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : fallback }
const filters = reactive({ since: initialDate(route.query.since, day(-29)), until: initialDate(route.query.until, day(0)), platform: platforms.includes(route.query.platform) ? route.query.platform : '', q: '', sort: 'increment' })
const applied = ref(null)
const items = ref([])
const loading = ref(false)
const error = ref('')
const page = ref(1)
const target = ref(null)
const history = ref(null)
const history_loading = ref(false)
const history_error = ref('')
let request_id = 0
let history_id = 0
const platform_options = computed(() => [{ value: '', label: text('allPlatforms') }, ...platforms.map(value => ({ value, label: names[value] }))])
const sort_options = computed(() => ['increment', 'total', 'published'].map(value => ({ value, label: text(`sort_${value}`) })))
const visible = computed(() => {
  const needle = filters.q.trim().toLocaleLowerCase()
  const rows = items.value.filter(item => (!filters.platform || item.platform === filters.platform)
    && (!needle || [item.title, item.video_key, item.channel].some(value => String(value ?? '').toLocaleLowerCase().includes(needle))))
  return rows.sort((left, right) => {
    const delta = filters.sort === 'published' ? (Date.parse(right.published_date ?? right.published_at) || 0) - (Date.parse(left.published_date ?? left.published_at) || 0)
      : Number(right[filters.sort === 'total' ? 'total_views' : 'increment_views']) - Number(left[filters.sort === 'total' ? 'total_views' : 'increment_views'])
    return delta || left.key.localeCompare(right.key)
  })
})
const pages = computed(() => Math.max(1, Math.ceil(visible.value.length / 20)))
const rows = computed(() => visible.value.slice((page.value - 1) * 20, page.value * 20))
const columns = computed(() => [
  { key: 'title', label: text('video'), width: '330px', wrap: true },
  { key: 'platform', label: text('platform'), width: '110px' },
  { key: 'channel', label: text('channel'), width: '160px', wrap: true },
  { key: 'increment_views', label: text('rangeViews'), width: '140px', align: 'right' },
  { key: 'total_views', label: text('totalViews'), width: '140px', align: 'right' },
  { key: 'actions', label: text('details'), width: '120px', align: 'right' },
])
const dates = computed(() => history.value?.daily.map(row => row.date) ?? [])
const trend = key => [{ label: text(key === 'increment_views' ? 'dailyViews' : 'snapshotViews'), values: history.value?.daily.map(row => row[key]) ?? [] }]
const number = value => value === null || value === undefined ? '—' : new Intl.NumberFormat(locale.value).format(value)
const summary = computed(() => ({ total: visible.value.reduce((sum, row) => sum + row.total_views, 0), increment: visible.value.reduce((sum, row) => sum + row.increment_views, 0) }))
watch(() => [filters.q, filters.platform, filters.sort], () => { page.value = 1 })
watch(pages, value => { page.value = Math.min(page.value, value) })
onMounted(load)
onBeforeUnmount(() => { request_id += 1; history_id += 1 })
async function load() {
  const id = ++request_id
  error.value = ''
  const duration = Date.parse(filters.until) - Date.parse(filters.since)
  if (!Number.isFinite(duration) || duration < 0 || duration > 365 * 86400000) { error.value = text('invalidRange'); return }
  loading.value = true
  const period = { since: filters.since, until: filters.until }
  try {
    const query = { ...calendar_report_range(period.since, period.until), traffic_scope: 'range' }
    const results = await Promise.all(platforms.map(async platform => {
      const result = await getConversionFunnelTrafficVideos({ ...query, platform, include_empty: 'true' })
      return result.items.map(item => ({ ...item, platform, key: `${platform}:${item.video_key}` }))
    }))
    if (id !== request_id) return
    items.value = results.flat()
    applied.value = period
    page.value = 1
  } catch (failure) { if (id === request_id) error.value = failure.message }
  finally { if (id === request_id) loading.value = false }
}
async function inspect(item) {
  target.value = item
  history.value = null
  await loadHistory()
}
async function loadHistory() {
  const id = ++history_id
  const item = target.value
  if (!item || !applied.value) return
  history_loading.value = true
  history_error.value = ''
  try {
    const result = await getTrafficVideoHistory({ platform: item.platform, video_key: item.video_key, calendar_since: applied.value.since, calendar_until: applied.value.until })
    if (id === history_id) history.value = result
  } catch (failure) { if (id === history_id) history_error.value = failure.message }
  finally { if (id === history_id) history_loading.value = false }
}
function close() { history_id += 1; target.value = null; history.value = null; history_loading.value = false }
function safeUrl(value) {
  try { const url = new URL(value); return ['http:', 'https:'].includes(url.protocol) ? url.href : null }
  catch { return null }
}
</script>

<template>
  <div class="vga-page social-videos">
    <PageHeader :title="text('title')" />
    <Toolbar>
      <FormField :label="text('since')" for="social-since"><Input id="social-since" v-model="filters.since" type="date" /></FormField>
      <FormField :label="text('until')" for="social-until"><Input id="social-until" v-model="filters.until" type="date" /></FormField>
      <template #actions><Button :loading="loading" @click="load">{{ text('query') }}</Button></template>
    </Toolbar>
    <Toolbar>
      <FormField :label="text('search')" for="social-search"><Input id="social-search" v-model="filters.q" type="search" :placeholder="text('searchHint')" /></FormField>
      <FormField :label="text('platform')"><Select v-model="filters.platform" :options="platform_options" :aria-label="text('platform')" /></FormField>
      <FormField :label="text('sort')"><Select v-model="filters.sort" :options="sort_options" :aria-label="text('sort')" /></FormField>
    </Toolbar>
    <p class="social-videos__note">{{ text('note') }}</p>
    <div v-if="loading" class="vga-state" role="status"><Spinner />{{ text('loading') }}</div>
    <div v-else-if="error" class="vga-state" role="alert"><p class="vga-error">{{ error }}</p><Button @click="load">{{ text('retry') }}</Button></div>
    <template v-else-if="applied">
      <div class="social-videos__summary"><strong>{{ text('summary', { count: visible.length, increment: number(summary.increment), total: number(summary.total) }) }}</strong><span>{{ applied.since }} — {{ applied.until }}</span></div>
      <Section :padded="false">
        <EmptyState v-if="!visible.length" :title="text('empty')" />
        <Table v-else :columns="columns" :rows="rows" row-key="key" data-testid="social-video-list">
          <template #title="{ row }"><div class="social-videos__identity"><strong>{{ row.title || row.video_key }}</strong><small>{{ row.video_key }}</small></div></template>
          <template #platform="{ row }">{{ names[row.platform] }}</template>
          <template #increment_views="{ row }"><span>{{ number(row.increment_views) }}</span><small v-if="row.baseline_missing" class="social-videos__missing">{{ text('missingBaseline') }}</small></template>
          <template #total_views="{ row }">{{ number(row.total_views) }}</template>
          <template #actions="{ row }"><Button size="sm" variant="secondary" :data-testid="`video-trend-${row.key}`" @click="inspect(row)">{{ text('trend') }}</Button></template>
        </Table>
        <Pagination :has-prev="page > 1" :has-next="page < pages" :summary="text('pager', { page, pages })" :prev-label="text('prev')" :next-label="text('next')" @prev="page -= 1" @next="page += 1" />
      </Section>
    </template>
    <Modal :open="Boolean(target)" :title="target?.title || target?.video_key || text('trend')" width="1000px" @update:open="close">
      <template v-if="target">
        <p>{{ names[target.platform] }} · {{ target.channel || '—' }}</p>
        <a v-if="safeUrl(target.url)" :href="safeUrl(target.url)" target="_blank" rel="noopener noreferrer">{{ text('original') }}</a>
        <div v-if="history_loading" class="vga-state" role="status"><Spinner />{{ text('loading') }}</div>
        <div v-else-if="history_error" role="alert"><p class="vga-error">{{ history_error }}</p><Button @click="loadHistory">{{ text('retry') }}</Button></div>
        <template v-else-if="history">
          <p>{{ text('latest', { views: number(history.total_views), date: history.latest_date || '—' }) }}</p>
          <p class="social-videos__note">{{ text('dailyNote') }}</p>
          <EmptyState v-if="!history.daily.some(row => row.total_views !== null)" :title="text('noSnapshots')" />
          <template v-else>
            <h3>{{ text('dailyViews') }}</h3><EmptyState v-if="!history.daily.some(row => row.increment_views !== null)" :title="text('noDailyGrowth')" /><DashboardTrend v-else :dates="dates" :series="trend('increment_views')" />
            <h3>{{ text('snapshotViews') }}</h3><DashboardTrend :dates="dates" :series="trend('total_views')" />
          </template>
        </template>
      </template>
    </Modal>
  </div>
</template>

<style scoped>
.social-videos__note { margin: 0; color: var(--drama-color-text-muted); font-size: var(--drama-font-size-sm); line-height: var(--drama-line-height-normal); }
.social-videos__summary { display: flex; flex-wrap: wrap; justify-content: space-between; gap: var(--drama-space-md); }
.social-videos__identity { display: grid; gap: var(--drama-space-xs); overflow-wrap: anywhere; }
.social-videos__identity small,.social-videos__missing { color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); }
.social-videos__missing { display: block; }
.social-videos a { color: var(--drama-color-primary); }
</style>
